import { Injectable }    from '@angular/core';
import {
	EnumCheckTypeEnum,
	IDtoTmoModelMapType,
	IOpenApiCheckResult,
	ObjectTypeEnum
}                        from '@app/core/interfaces/open-api.interface';
import { CheckEnum }     from '@app/core/models/open-api.model';
import { LoggerService } from '@app/shared/services/logger.service';
import { APP_CONFIG }    from '@appConfig/_app-config_master';
import { APP_MODE }      from '@appConfig/app-mode-config';
import { SortOrderEnum } from '@shared/list-operations/components/sort-filter/sort-filter.model';
import {
	IToasterConfig,
	ToasterService
}                        from '@shared/services/toaster.service';
import {
	EnumUtils,
	ObjectUtils
}                        from '@utils/helpers';

@Injectable()
export class OpenApiCheckerService {

	private readonly dtoCheckList: IDtoTmoModelMapType[];

	constructor(
		protected readonly logger: LoggerService,
		protected readonly toaster: ToasterService
	) {
		this.dtoCheckList = APP_CONFIG.openApi.dtoCheckList;
	}

	private createDefaultCheckResult( modelMap: IDtoTmoModelMapType ): IOpenApiCheckResult {
		let modelNameTmo = '';

		switch ( modelMap.type ) {
			case ObjectTypeEnum.CLASS:
				modelNameTmo = modelMap.tmo?.constructor?.name;
				break;
			case ObjectTypeEnum.ENUM:
				modelNameTmo = modelMap?.tmoEnumName;
				break;
		}

		const checkResult: IOpenApiCheckResult = {
			modelNameDto: modelMap?.dto?.constructor?.name?.replace( APP_CONFIG.openApi.dtoClassPrefix, '' ),
			modelNameTmo,
			modelsInSync: true,
			countMismatch: 0,
			recursivePropsMismatch: [],
			newDtoProps: [],
			removedDtoProps: [],
			enumMismatch: [],
			resultMessage: null
		};

		return checkResult;
	}

	private comparePropertyCount( checkResult: IOpenApiCheckResult, dtoKeys: string[], tmoKeys: string[] ) {
		if ( dtoKeys?.length !== tmoKeys?.length ) {
			checkResult.modelsInSync = false;
			checkResult.countMismatch = Math.abs( dtoKeys?.length - tmoKeys?.length );
		}
	}

	private checkForNewDtoProps( checkResult: IOpenApiCheckResult, dtoKeys: string[], tmoKeys: string[] ) {
		dtoKeys.forEach( ( key ) => {
			if ( !tmoKeys.includes( key ) ) {
				checkResult.modelsInSync = false;
				checkResult.newDtoProps.push( key );
			}
		} );
	}

	private checkForRemovedDtoProps( checkResult: IOpenApiCheckResult, dtoKeys: string[], tmoKeys: string[] ) {
		tmoKeys.forEach( ( key ) => {
			if ( !dtoKeys.includes( key ) ) {
				checkResult.modelsInSync = false;
				checkResult.removedDtoProps.push( key );
			}
		} );
	}

	private checkRecursivelyDtoTmoProps( checkResult: IOpenApiCheckResult, dto: object, tmo: object ): void {

		const dtoKeys = ObjectUtils.recursivelyGetAllObjectsKeyPaths( dto );
		const tmoKeys = ObjectUtils.recursivelyGetAllObjectsKeyPaths( tmo );

		function compareObjectKeys( keysA: string[], keysB: string[] ): void {
			checkResult.modelsInSync = false;
			keysA.forEach( keyA => {
				if ( !keysB.includes( keyA ) ) {
					checkResult.recursivePropsMismatch.push( keyA );
				}
			} );
		}

		if ( dtoKeys.length > tmoKeys.length ) {
			compareObjectKeys( dtoKeys, tmoKeys );
		}

		if ( dtoKeys.length < tmoKeys.length ) {
			compareObjectKeys( tmoKeys, dtoKeys );
		}
	}

	private compareEnums(
		checkResult: IOpenApiCheckResult,
		checkFor: EnumCheckTypeEnum,
		dtoEnum: object,
		tmoEnum: object,
		excludeDtoEnumValuesFromCheckList?: string[],
		excludeTmoEnumValuesFromCheckList?: string[]
	): void {
		let dtoEnumList;
		let tmoEnumList;

		switch ( checkFor ) {
			case EnumCheckTypeEnum.CheckForKeys:
				dtoEnumList = EnumUtils.getEnumsKeysAsList( dtoEnum, SortOrderEnum.ASC );
				tmoEnumList = EnumUtils.getEnumsKeysAsList( tmoEnum, SortOrderEnum.ASC );

				// gets enum keys from enum values for check excludes
				if ( excludeDtoEnumValuesFromCheckList ) {
					excludeDtoEnumValuesFromCheckList =
						EnumUtils.getEnumKeysByValues( dtoEnum, excludeDtoEnumValuesFromCheckList );
				}
				if ( excludeTmoEnumValuesFromCheckList ) {
					excludeTmoEnumValuesFromCheckList =
						EnumUtils.getEnumKeysByValues( tmoEnum, excludeTmoEnumValuesFromCheckList );
				}

				break;
			case EnumCheckTypeEnum.CheckForValues:
				dtoEnumList = EnumUtils.getEnumsValuesAsList( dtoEnum, SortOrderEnum.ASC );
				tmoEnumList = EnumUtils.getEnumsValuesAsList( tmoEnum, SortOrderEnum.ASC );

				break;
		}

		// filters DTO excludes
		if ( excludeDtoEnumValuesFromCheckList ) {
			dtoEnumList = dtoEnumList.filter( dtoEnumItem => !excludeDtoEnumValuesFromCheckList.includes( dtoEnumItem ) );
		}

		// filters TMO excludes
		if ( excludeTmoEnumValuesFromCheckList ) {
			tmoEnumList = tmoEnumList.filter( tmoEnumItem => !excludeTmoEnumValuesFromCheckList.includes( tmoEnumItem ) );
		}


		if ( dtoEnumList.length !== tmoEnumList.length ) {
			this.comparePropertyCount( checkResult, dtoEnumList, tmoEnumList );
		}

		tmoEnumList.forEach( tmoItem => {
			if ( !dtoEnumList.includes( tmoItem ) ) {
				checkResult.modelsInSync = false;
				checkResult.enumMismatch.push( `checkType: ${ checkFor } | ${ tmoItem } exist in TMO but not in DTO` );
			}
		} );

		dtoEnumList.forEach( dtoItem => {
			if ( !tmoEnumList.includes( dtoItem ) ) {
				checkResult.modelsInSync = false;
				checkResult.enumMismatch.push( `checkType: ${ checkFor } | ${ dtoItem } exist in DTO but not in TMO` );
			}
		} );
	}


	runDefaultCheck( enforceCheck = false ) {
		if ( APP_CONFIG.appMode === APP_MODE.LOCAL || enforceCheck ) {
			console.groupCollapsed( 'runDefaultCheck' );
			const result = this.checkListOfDto( this.dtoCheckList );
			const message = this.getListResultMessage( result ).trim();
			if ( message.length ) {
				const toasterConfigOverwrite: IToasterConfig = {
					closeButton: false,
					enableHtml: true,
					messageClass: null,
					progressBar: true,
					tapToDismiss: true,
					timeOut: 15000,
					titleClass: 'h4'
				};

				this.toaster.info(
					'Filter console output by "DTO/TMO check result".<br><br>ATTENTION!!!<br>Enum checks may fail due to different' +
					' CaseStyles either on keys or values. For now please ignore reports on key and check on value cases.',
					'OpenApi Check Failed',
					toasterConfigOverwrite
				);
				// keep console.log() here for formatted output
				console.log( 'DTO/TMO check result:\n\n' + message );
			} else {
				// keep console.log() here for formatted output
				console.log( 'DTO/TMO check success. Both model types are in sync' );
			}
			console.groupEnd();
		}
	}

	getListResultMessage( listResult: IOpenApiCheckResult[] ): string {
		let listResultMessage = '';

		listResult.forEach( ( resultItem: IOpenApiCheckResult ) => {
			if ( !resultItem.modelsInSync ) {
				listResultMessage += resultItem.resultMessage + '\n\n';
			}
		} );

		return listResultMessage;
	}

	checkListOfDto( checkList: IDtoTmoModelMapType[] ): IOpenApiCheckResult[] {
		const listResult: IOpenApiCheckResult[] = [];

		checkList.forEach( ( modelMap: IDtoTmoModelMapType ) => {
			listResult.push( this.checkSpecificDto( modelMap ) );
		} );

		return listResult;
	}

	checkSpecificDto( modelMap: IDtoTmoModelMapType ): IOpenApiCheckResult {
		const checkResult = this.createDefaultCheckResult( modelMap );

		// get model keys
		const dtoKeys = Object.keys( modelMap.dto );
		const tmoKeys = Object.keys( modelMap.tmo );

		// checks
		if ( modelMap.checkList.includes( CheckEnum.MODEL_PROPERTY_COUNT ) || modelMap.checkList.includes( CheckEnum.MODEL_CHECK_ALL ) ) {
			this.comparePropertyCount( checkResult, dtoKeys, tmoKeys );
		}
		if ( modelMap.checkList.includes( CheckEnum.MODEL_NEW_PROPS ) || modelMap.checkList.includes( CheckEnum.MODEL_CHECK_ALL ) ) {
			this.checkForNewDtoProps( checkResult, dtoKeys, tmoKeys );
		}
		if ( modelMap.checkList.includes( CheckEnum.MODEL_REMOVED_PROPS ) || modelMap.checkList.includes( CheckEnum.MODEL_CHECK_ALL ) ) {
			this.checkForRemovedDtoProps( checkResult, dtoKeys, tmoKeys );
		}
		if ( modelMap.checkList.includes( CheckEnum.MODEL_RECURSIVELY_COMPARE_PROPS ) || modelMap.checkList.includes( CheckEnum.MODEL_CHECK_ALL ) ) {
			// currently this check cannot be run, DTOs does not have nested objects (initialized with undefined on first level)
			// this.checkRecursivelyDtoTmoProps( checkResult, modelMap.dto, modelMap.tmo );
		}

		if ( modelMap.checkList.includes( CheckEnum.ENUM_COMPARE_KEYS ) || modelMap.checkList.includes( CheckEnum.ENUM_CHECK_ALL ) ) {
			this.compareEnums(
				checkResult, EnumCheckTypeEnum.CheckForKeys, modelMap.dto, modelMap.tmo, modelMap?.excludeDtoPropsFromCheckList,
				modelMap?.excludeTmoPropsFromCheckList
			);
		}

		if ( modelMap.checkList.includes( CheckEnum.ENUM_COMPARE_VALUES ) || modelMap.checkList.includes( CheckEnum.ENUM_CHECK_ALL ) ) {
			this.compareEnums(
				checkResult, EnumCheckTypeEnum.CheckForValues, modelMap.dto, modelMap.tmo, modelMap?.excludeDtoPropsFromCheckList,
				modelMap?.excludeTmoPropsFromCheckList
			);
		}


		// resultMessage
		if ( !checkResult.modelsInSync ) {
			let resultMessage = '';
			resultMessage += checkResult.modelNameDto
											 ? `####### TMO/DTO Identifier: ${ checkResult.modelNameTmo ? checkResult.modelNameTmo + '/' : '' }${ checkResult.modelNameDto }\n`
											 : '';
			resultMessage += checkResult.countMismatch
											 ? `Props count difference between DTO/TMO: ${ checkResult.countMismatch }\n`
											 : '';
			resultMessage += checkResult.removedDtoProps.length
											 ? `Removed props in DTO: ${ '\n-> ' + checkResult.removedDtoProps.join( '\n-> ' ) }\n`
											 : '';
			resultMessage += checkResult.newDtoProps.length
											 ? `Newly added props in DTO: ${ '\n-> ' + checkResult.newDtoProps.join( '\n-> ' ) }\n`
											 : '';
			resultMessage += checkResult.recursivePropsMismatch.length
											 ? `Recursively checked props failures: ${ '\n-> ' + checkResult.recursivePropsMismatch.join( '\n-> ' ) }`
											 : '';
			resultMessage += checkResult.enumMismatch.length
											 ? `Enum check failures: ${ '\n-> ' + checkResult.enumMismatch.join( '\n-> ' ) }`
											 : '';

			checkResult.resultMessage = resultMessage;
		}

		return checkResult;
	}

}

